"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useState, useTransition } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";

import { register } from "@/actions/register";
import { PasswordCriteria } from "@/components/auth/PasswordCriteria";
import { PasswordField } from "@/components/auth/PasswordField";
import { FormError } from "@/components/FormError";
import { FormSuccess } from "@/components/FormSuccess";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { env } from "@/lib/env";
import { nonce } from "@/lib/utils";
import { FIRST_AND_LAST_NAME_MAX_LENGTH, FIRST_AND_LAST_NAME_MIN_LENGTH, RegisterSchema, TRegisterSchema } from "@/schemas";

export const RegisterForm = () => {
    const t = useTranslations("auth");

    const [ error, setError ] = useState<string | undefined>("");
    const [ success, setSuccess ] = useState<string | undefined>("");
    const [ captchaToken, setCaptchaToken ] = useState<string | null>(null);
    const [ isPending, startTransition ] = useTransition();

    const formSchema = RegisterSchema(t);
    const form = useForm<TRegisterSchema>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: "",
        },
    });

    const onSubmit = (values: TRegisterSchema) => {
        if (!captchaToken) {
            setError("Veuillez compléter le CAPTCHA.");
            return;
        }

        startTransition(() => {
            register({ ...values, captchaToken }).then(data => {
                setError(data.error);
                setSuccess(data.success);

                if (data.success) {
                    form.reset();
                    setCaptchaToken(null);
                }
            });
        });
    };

    return (
        <Form { ...form }>
            <form onSubmit={ form.handleSubmit(onSubmit) } className="space-y-6">
                <div className="space-y-4">
                    <div className="flex gap-4">
                        <FormField
                            control={ form.control }
                            name="firstName"
                            render={ ({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("common.firstNameLabel")}</FormLabel>
                                    <FormControl>
                                        <Input { ...field } disabled={ isPending } autoComplete="given-name" />
                                    </FormControl>
                                    <FormDescription>
                                        {t("common.firstNameDescription", { min: FIRST_AND_LAST_NAME_MIN_LENGTH, max: FIRST_AND_LAST_NAME_MAX_LENGTH })}
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            ) }
                        />
                        <FormField
                            control={ form.control }
                            name="lastName"
                            render={ ({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("common.lastNameLabel")}</FormLabel>
                                    <FormControl>
                                        <Input { ...field } disabled={ isPending } autoComplete="family-name" />
                                    </FormControl>
                                    <FormDescription>
                                        {t("common.lastNameDescription", { min: FIRST_AND_LAST_NAME_MIN_LENGTH, max: FIRST_AND_LAST_NAME_MAX_LENGTH })}
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            ) }
                        />
                    </div>
                    <FormField
                        control={ form.control }
                        name="email"
                        render={ ({ field }) => (
                            <FormItem>
                                <FormLabel>{t("common.emailLabel")}</FormLabel>
                                <FormControl>
                                    <Input { ...field } disabled={ isPending } type="email" autoComplete="email" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        ) }
                    />
                    <FormField
                        control={ form.control }
                        name="password"
                        render={ ({ field }) => (
                            <FormItem>
                                <FormLabel>{t("common.passwordLabel")}</FormLabel>
                                <FormControl>
                                    <PasswordField { ...field } isPending={ isPending } autoComplete="new-password" />
                                </FormControl>
                                <FormMessage />
                                <PasswordCriteria password={ field.value } />
                            </FormItem>
                        ) }
                    />
                </div>
                <div className="flex justify-center">
                    <ReCAPTCHA
                        nonce={ nonce }
                        sitekey={ env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY }
                        onChange={ (token) => setCaptchaToken(token) }
                        onExpired={ () => setCaptchaToken(null) }
                    />
                </div>

                <FormError message={ error } />
                <FormSuccess message={ success } />
                <Button disabled={ isPending } type="submit" className="w-full">
                    {t("register.submitButton")}
                </Button>
            </form>
        </Form>
    );
};